import { useKnowledgeDocumentDataContext } from 'features/knowledgeDocumentViewer/contexts/KnowledgeDocumentDataContext'
import { usePipelineConfig } from 'features/knowledgeDocumentViewer/contexts/PipelineConfigContext'
import type { SystemDocumentEntry } from 'features/knowledgeDocumentViewer/types/systemDocumentEntries'
import { parseSystemDocumentEntries } from 'features/knowledgeDocumentViewer/types/systemDocumentEntrySchemas'
import {
  partitionSystemDocumentLookupIds,
  systemDocumentsByDocumentIds,
  systemDocumentsByEntryIds,
} from 'features/knowledgeDocumentViewer/utils/documentEntryLookups'
import { resolveSystemDocumentsFromList } from 'features/knowledgeDocumentViewer/utils/resolveSystemDocumentsFromList'
import { useGetRolodexEntriesByLoadOptions } from 'features/rolodex'
import { useMemo, useState } from 'react'

const BY_ID_FETCH_RETRY = { shouldRetryOnError: true, errorRetryCount: 2 }
const EMPTY_FETCHED_ENTRIES: SystemDocumentEntry[] = []

export type FetchedByIdCache = {
  siteId: string
  entries: SystemDocumentEntry[]
}

export function nextFetchedByIdCache({
  cache,
  incoming,
  siteId,
  stableIds,
}: {
  cache: FetchedByIdCache
  incoming: readonly SystemDocumentEntry[]
  siteId: string
  stableIds: readonly string[]
}): FetchedByIdCache {
  const incomingForSite = incoming.filter((entry) => {
    const site = entry.entry.tags.site
    return site === null || site === undefined || site === siteId
  })
  const base = cache.siteId === siteId ? cache.entries : []
  const merged = mergeSystemDocumentEntries(base, incomingForSite)
  const entries = resolveSystemDocumentsFromList(merged, [...stableIds]).found
  if (
    cache.siteId === siteId &&
    cache.entries.length === entries.length &&
    cache.entries.every((entry, index) => entry === entries[index])
  ) {
    return cache
  }
  return { siteId, entries }
}

/** Resolve system-documents by BQ document_id, keeping already-fetched entries while more ids load. */
export function useSystemDocumentsByBqIds(documentIds: readonly string[]): {
  documents: SystemDocumentEntry[] | undefined
  isLoading: boolean
} {
  const { systemDocumentEntries, siteID } = useKnowledgeDocumentDataContext()
  const { getPipelineConfigScopedTags, isPipelineConfigScopeLoading } =
    usePipelineConfig()
  const [fetchedByIdCache, setFetchedByIdCache] = useState<FetchedByIdCache>({
    siteId: siteID,
    entries: [],
  })

  const stableIds = useMemo(
    () => Array.from(new Set(documentIds.filter((id) => id.length > 0))),
    [documentIds]
  )

  const cacheEntriesForSite =
    fetchedByIdCache.siteId === siteID
      ? fetchedByIdCache.entries
      : EMPTY_FETCHED_ENTRIES

  const idsToFetch = useMemo(() => {
    if (stableIds.length === 0) {
      return []
    }
    return resolveSystemDocumentsFromList(
      [...systemDocumentEntries, ...cacheEntriesForSite],
      stableIds
    ).missingIds
  }, [cacheEntriesForSite, stableIds, systemDocumentEntries])

  const { documentIds: documentIdsToFetch, entryIds: entryIdsToFetch } =
    useMemo(() => partitionSystemDocumentLookupIds(idsToFetch), [idsToFetch])

  const scopedTags = useMemo(() => {
    if (isPipelineConfigScopeLoading || idsToFetch.length === 0) {
      return undefined
    }
    return getPipelineConfigScopedTags({ site: siteID })
  }, [
    getPipelineConfigScopedTags,
    idsToFetch.length,
    isPipelineConfigScopeLoading,
    siteID,
  ])

  const documentIdLoadOptions = useMemo(() => {
    if (!scopedTags) return undefined
    return systemDocumentsByDocumentIds(
      documentIdsToFetch,
      scopedTags,
      documentIdsToFetch.length
    )
  }, [documentIdsToFetch, scopedTags])

  const entryIdLoadOptions = useMemo(() => {
    if (!scopedTags) return undefined
    return systemDocumentsByEntryIds(entryIdsToFetch, scopedTags)
  }, [entryIdsToFetch, scopedTags])

  const {
    data: fetchedByDocumentIdRaw,
    error: errorByDocumentId,
    isLoading: isLoadingByDocumentId,
  } = useGetRolodexEntriesByLoadOptions<unknown>(
    documentIdLoadOptions,
    BY_ID_FETCH_RETRY
  )
  const {
    data: fetchedByEntryIdRaw,
    error: errorByEntryId,
    isLoading: isLoadingByEntryId,
  } = useGetRolodexEntriesByLoadOptions<unknown>(
    entryIdLoadOptions,
    BY_ID_FETCH_RETRY
  )

  const fetchedByDocumentId = useMemo(
    () =>
      fetchedByDocumentIdRaw === undefined
        ? undefined
        : parseSystemDocumentEntries(fetchedByDocumentIdRaw),
    [fetchedByDocumentIdRaw]
  )
  const fetchedByEntryId = useMemo(
    () =>
      fetchedByEntryIdRaw === undefined
        ? undefined
        : parseSystemDocumentEntries(fetchedByEntryIdRaw),
    [fetchedByEntryIdRaw]
  )

  const waitingOnByIdFetch =
    idsToFetch.length > 0 &&
    ((documentIdLoadOptions !== undefined &&
      errorByDocumentId === undefined &&
      (isLoadingByDocumentId || fetchedByDocumentId === undefined)) ||
      (entryIdLoadOptions !== undefined &&
        errorByEntryId === undefined &&
        (isLoadingByEntryId || fetchedByEntryId === undefined)))

  const nextCache = nextFetchedByIdCache({
    cache: fetchedByIdCache,
    incoming: [...(fetchedByDocumentId ?? []), ...(fetchedByEntryId ?? [])],
    siteId: siteID,
    stableIds,
  })
  if (nextCache !== fetchedByIdCache) {
    setFetchedByIdCache(nextCache)
  }

  const documents = useMemo(() => {
    if (stableIds.length === 0) {
      return undefined
    }
    const found = resolveSystemDocumentsFromList(
      [...systemDocumentEntries, ...nextCache.entries],
      stableIds
    ).found
    if (
      found.length === 0 &&
      (isPipelineConfigScopeLoading || waitingOnByIdFetch)
    ) {
      return undefined
    }
    return found
  }, [
    isPipelineConfigScopeLoading,
    nextCache.entries,
    stableIds,
    systemDocumentEntries,
    waitingOnByIdFetch,
  ])

  const isLoading =
    stableIds.length > 0 &&
    (isPipelineConfigScopeLoading ||
      waitingOnByIdFetch ||
      documents === undefined)

  return { documents, isLoading }
}

export function useSystemDocumentByBqId(documentId: string | undefined): {
  document: SystemDocumentEntry | undefined
  isLoading: boolean
} {
  const ids = useMemo(() => (documentId ? [documentId] : []), [documentId])
  const { documents, isLoading } = useSystemDocumentsByBqIds(ids)
  return {
    document: documents?.[0],
    isLoading: !!documentId && isLoading,
  }
}

function mergeSystemDocumentEntries(
  current: SystemDocumentEntry[],
  incoming: readonly SystemDocumentEntry[]
): SystemDocumentEntry[] {
  if (incoming.length === 0) return current
  const byId = new Map(current.map((entry) => [entry.entry.id, entry]))
  for (const entry of incoming) {
    byId.set(entry.entry.id, entry)
  }
  return Array.from(byId.values())
}
